import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Small parts every default-template page uses, drawn from raisonne-DS "Raisonné · Components":
 * Button, Arrow link, Section header, Catalogue card, Badge, Stat, Quote card.
 */

type ButtonVariant = 'primary' | 'outline' | 'light';

const BUTTON: Record<ButtonVariant, string> = {
  primary: 'bg-neutral-900 text-neutral-50 hover:bg-neutral-800 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-200',
  outline:
    'border border-neutral-300 bg-white text-neutral-950 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-50 dark:hover:bg-neutral-900',
  light: 'bg-neutral-100 text-neutral-900 hover:bg-white',
};

export function SzButton({
  href,
  children,
  variant = 'primary',
  size = 'lg',
  arrow = false,
  icon,
  external = false,
  className = '',
}: {
  href: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  arrow?: boolean;
  icon?: ReactNode;
  external?: boolean;
  className?: string;
}) {
  const height = size === 'lg' ? 'h-9' : size === 'md' ? 'h-8' : 'h-7';
  const cls = `inline-flex ${height} shrink-0 items-center justify-center gap-1.5 rounded-[10px] px-2.5 text-[14px] font-medium leading-5 transition-colors ${BUTTON[variant]} ${className}`;
  const inner = (
    <>
      {icon}
      {children}
      {arrow && <ArrowRight className="size-4" aria-hidden />}
    </>
  );
  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {inner}
    </a>
  ) : (
    <Link href={href} className={cls}>
      {inner}
    </Link>
  );
}

export function SzArrowLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      href={href}
      className="inline-flex shrink-0 items-center gap-1 text-[12.8px] font-medium leading-[19.2px] text-neutral-900 hover:text-neutral-500 dark:text-neutral-100 dark:hover:text-neutral-400"
    >
      {children}
      <ArrowRight className="size-3.5" aria-hidden />
    </Link>
  );
}

export function SzSectionHeader({
  title,
  description,
  action,
  small = false,
  as: Tag = 'h2',
}: {
  title: string;
  description?: string | null;
  action?: { label: string; href: string } | null;
  small?: boolean;
  as?: 'h1' | 'h2' | 'h3';
}) {
  return (
    <div className="flex w-full items-end justify-between gap-6">
      <div className="flex min-w-0 flex-col gap-1">
        <Tag
          className={
            small
              ? 'text-[18px] font-semibold leading-7 tracking-[-0.45px] text-neutral-950 dark:text-neutral-50'
              : 'text-[24px] font-semibold leading-8 tracking-[-0.6px] text-neutral-950 dark:text-neutral-50'
          }
        >
          {title}
        </Tag>
        {description && <p className="max-w-[36rem] text-[16px] leading-6 text-neutral-500 dark:text-neutral-400">{description}</p>}
      </div>
      {action && <SzArrowLink href={action.href}>{action.label}</SzArrowLink>}
    </div>
  );
}

export function SzBadge({ children, tone = 'solid' }: { children: ReactNode; tone?: 'solid' | 'outline' }) {
  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center gap-1 rounded-full px-2 text-[12px] font-medium leading-4 ${
        tone === 'outline'
          ? 'border border-neutral-300 text-neutral-950 dark:border-neutral-700 dark:text-neutral-50'
          : 'bg-neutral-100 text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100'
      }`}
    >
      {children}
    </span>
  );
}

/** Some records keep a film in a picture field; those play as a muted loop. */
export const isVideoUrl = (url?: string | null) => Boolean(url && /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url));

/** Artwork is never cropped. Intrinsic layout follows the original file's aspect ratio. */
export function SzMedia({ url, alt = '', layout = 'fill', eager = false, className = '' }: { url?: string | null; alt?: string; layout?: 'intrinsic' | 'fill'; eager?: boolean; className?: string }) {
  if (!url) return null;
  const cls = `${layout === 'intrinsic' ? 'block h-auto w-full' : 'absolute inset-0 size-full'} object-contain ${className}`;
  return isVideoUrl(url) ? (
    <video src={url} muted loop playsInline autoPlay preload="metadata" aria-label={alt || undefined} className={cls} />
  ) : (
    <img src={url} alt={alt} loading={eager ? 'eager' : 'lazy'} className={cls} />
  );
}

export interface SzCardItem {
  title: string;
  meta?: string | null;
  href: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
  status?: string | null;
}

/** One record in a grid: media above, caption below (never a boxed card). */
export function SzCatalogueCard({ item, priority = false }: { item: SzCardItem; aspect?: 'square' | 'wide' | 'tall'; priority?: boolean }) {
  return (
    <Link href={item.href} className="group flex min-w-0 flex-col gap-2.5">
      <span className="relative block w-full overflow-hidden rounded-[10px] bg-neutral-100 dark:bg-neutral-900" data-artwork-frame="intrinsic">
        <SzMedia url={item.videoUrl ?? item.imageUrl} alt={item.title} layout="intrinsic" eager={priority} />
        {item.status && (
          <span className="absolute left-1.5 top-1.5">
            <SzBadge>{item.status}</SzBadge>
          </span>
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-[14px] font-medium leading-5 text-neutral-950 dark:text-neutral-50">{item.title}</span>
        {item.meta && <span className="truncate text-[12px] leading-4 text-neutral-500 dark:text-neutral-400">{item.meta}</span>}
      </span>
    </Link>
  );
}

export function SzStat({ value, label, description }: { value: string; label: string; description?: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-[36px] font-semibold leading-10 tracking-[-0.9px] text-neutral-950 tabular-nums dark:text-neutral-50">{value}</span>
      <span className="text-[14px] font-medium leading-5 text-neutral-950 dark:text-neutral-50">{label}</span>
      {description && <span className="text-[14px] leading-5 text-neutral-500 dark:text-neutral-400">{description}</span>}
    </div>
  );
}

export function SzQuoteCard({ quote, outlet, meta, href }: { quote: string; outlet?: string | null; meta?: string | null; href: string }) {
  return (
    <Link
      href={href}
      className="flex min-w-0 flex-1 flex-col gap-4 rounded-[10px] bg-neutral-50 p-6 transition-colors hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
    >
      <span className="text-[18px] leading-[29.25px] text-neutral-950 dark:text-neutral-50">{quote}</span>
      <span className="flex flex-col gap-0.5">
        {outlet && <span className="text-[14px] font-medium leading-5 text-neutral-950 dark:text-neutral-50">{outlet}</span>}
        {meta && <span className="text-[12px] leading-4 text-neutral-500 dark:text-neutral-400">{meta}</span>}
      </span>
    </Link>
  );
}

/** "1,234" */
export const szCount = (n: number) => n.toLocaleString('en-US');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "4 May 2026" */
export function szDate(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "5 Sep – 15 Nov 2026", "until 15 Dec 2026", "from 3 Feb 2027" */
export function szDateRange(start?: string | null, end?: string | null, now = new Date()): string | null {
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  const valid = (d: Date | null): d is Date => Boolean(d && !Number.isNaN(d.getTime()));
  if (valid(s) && valid(e)) {
    if (s <= now && e >= now) return `until ${szDate(end)}`;
    const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
    const startText = sameYear ? `${s.getUTCDate()} ${MONTHS[s.getUTCMonth()]}` : szDate(start);
    return `${startText} – ${szDate(end)}`;
  }
  if (valid(s)) return s > now ? `from ${szDate(start)}` : szDate(start);
  return null;
}
